<script setup lang="ts">
import type { TableColumn } from '../../composables/useTableColumns';

defineProps<{
  columns: TableColumn[];
}>();

const ROWS = 10;
const ROW_HEIGHT = 52;
const BONE_WIDTH = 0.7;
const MIN_BONE_WIDTH = 16;
const CELL_PADDING = 12;
const FADE_ROWS = 3;

const rowStyle = (row: number) => {
  const rowsFromEnd = ROWS - row;
  return {
    height: `${ROW_HEIGHT}px`,
    opacity: rowsFromEnd < FADE_ROWS ? (rowsFromEnd + 1) / (FADE_ROWS + 1) : 1,
  };
};

const boneStyle = (column: TableColumn, row: number, col: number) => {
  const available = Math.max(column.width - CELL_PADDING * 2, 0);
  return {
    left: `${column.left + CELL_PADDING}px`,
    width: `${Math.max(available * BONE_WIDTH, Math.min(available, MIN_BONE_WIDTH))}px`,
    animationDelay: `${-(row + col) * 0.07}s`,
  };
};
</script>

<template>
  <div class="table-skeleton" aria-hidden="true">
    <div
      v-for="row in ROWS"
      :key="row"
      class="table-skeleton__row"
      :style="rowStyle(row)"
    >
      <span
        v-for="(column, col) in columns"
        :key="col"
        class="table-skeleton__bone"
        :style="boneStyle(column, row, col)"
      />
    </div>
  </div>
</template>

<style scoped lang="scss">
$blue: #40a4c6;

.table-skeleton {
  position: relative;
  width: 100%;

  &__row {
    position: relative;
    border-bottom: 1px solid rgba($blue, 0.08);
  }

  &__bone {
    position: absolute;
    top: 50%;
    height: 0.7rem;
    border-radius: 999px;
    transform: translateY(-50%);
    background: linear-gradient(
      90deg,
      rgba($blue, 0.08) 0%,
      rgba($blue, 0.22) 40%,
      rgba($blue, 0.08) 80%
    );
    background-size: 200% 100%;
    animation: shimmer 1.6s ease-in-out infinite;
  }
}

@keyframes shimmer {
  0% {
    background-position: 100% 0;
  }
  100% {
    background-position: -100% 0;
  }
}

@media (prefers-reduced-motion: reduce) {
  .table-skeleton__bone {
    animation: none;
  }
}
</style>
